import { DeedRecord, PropertyTaxInformation } from '../graphql/types';

/**
 * Pure formatting / ordering / placeholder helpers for the Title Search (TR)
 * findings sections (Property Tax Information and Deed Information).
 *
 * These functions are intentionally side-effect free and deterministic so they
 * can be unit- and property-tested in isolation from the React components that
 * consume them.
 *
 * Requirements: 6.5, 6.9, 6.10, 6.11, 6.12, 7.1, 7.5, 7.6, 7.8, 7.9, 7.10
 */

/** Placeholder rendered in place of a missing/unavailable field value (6.11, 7.10). */
export const PLACEHOLDER = 'N/A';

/** Empty-state message when no property tax information was retrieved (6.10). */
export const NO_TAX_INFO_MESSAGE = 'Property tax information is not available.';

/** Empty-state message when a tax record contains zero installments (6.12). */
export const NO_INSTALLMENTS_MESSAGE = 'No installment data is available.';

/** Empty-state message when no deeds were found for the property (7.9). */
export const NO_DEEDS_MESSAGE = 'No deed information is available.';

/**
 * A value is "missing" when it is null/undefined or a blank/whitespace-only
 * string. Numbers (including 0) and booleans (including false) are NOT missing.
 */
function isMissing(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  return false;
}

/**
 * Coerce a monetary value (number, or Decimal-as-string from DynamoDB) into a
 * finite number, or null when it is missing / not a valid number.
 */
function toNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/** True when (year, month, day) is a valid calendar date. */
function isValidYmd(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/**
 * Format a monetary amount as a US-dollar currency string with exactly two
 * decimal places, e.g. 150000 -> "$150,000.00". Missing/invalid amounts render
 * the placeholder so the field stays present rather than hidden.
 *
 * Requirements: 6.5, 6.9, 7.8 (and 6.11 / 7.10 for the missing-value case)
 */
export function formatCurrency(value: number | string | null | undefined): string {
  const amount = toNumber(value);
  if (amount === null) {
    return PLACEHOLDER;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format a deed/recorded date (ISO 8601 string) as "MMM DD, YYYY"
 * (e.g. "May 14, 2023"). Missing/invalid dates render the placeholder.
 *
 * Date components are read directly (or via UTC) to avoid local-timezone shifts
 * that could move a date across a day boundary.
 *
 * Requirements: 7.5, 7.6 (and 7.10 for the missing-value case)
 */
const MONTH_ABBREVIATIONS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatYmd(year: number, month: number, day: number): string {
  const monthLabel = MONTH_ABBREVIATIONS[month - 1];
  const dayLabel = String(day).padStart(2, '0');
  return `${monthLabel} ${dayLabel}, ${year}`;
}

export function formatDeedDate(value: string | null | undefined): string {
  if (isMissing(value)) {
    return PLACEHOLDER;
  }
  const raw = String(value).trim();

  // Primary path: ISO date (optionally with a time component), e.g. "2023-05-14".
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    if (isValidYmd(Number(year), Number(month), Number(day))) {
      return formatYmd(Number(year), Number(month), Number(day));
    }
    return PLACEHOLDER;
  }

  // Fallback: anything else the Date parser understands, normalized via UTC.
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return formatYmd(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth() + 1,
      parsed.getUTCDate()
    );
  }

  return PLACEHOLDER;
}

/**
 * Return the value rendered as a string, or the placeholder when the value is
 * missing (null/undefined/blank). The field is never hidden.
 *
 * Requirements: 6.11, 7.10
 */
export function displayOrPlaceholder(
  value: string | number | boolean | null | undefined
): string {
  if (isMissing(value)) {
    return PLACEHOLDER;
  }
  return String(value);
}

/**
 * Resolve a deed's recorded date to a sortable timestamp. Deeds with a
 * missing/unparseable recorded date sort to the end (treated as +Infinity).
 */
function recordedTimestamp(deed: DeedRecord): number {
  const value = deed.recordedDate;
  if (isMissing(value)) {
    return Number.POSITIVE_INFINITY;
  }
  const raw = String(value).trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return Date.UTC(Number(year), Number(month) - 1, Number(day));
  }
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
}

/**
 * Order deeds for chain-of-title display: by recorded date from oldest to
 * newest. Returns a new array (the input is not mutated) containing exactly the
 * same set of deeds, with stable ordering for ties / undated records.
 *
 * Requirements: 7.1
 */
export function orderDeedsForChainOfTitle(deeds: DeedRecord[]): DeedRecord[] {
  return [...deeds].sort((a, b) => {
    const ta = recordedTimestamp(a);
    const tb = recordedTimestamp(b);
    if (ta === tb) {
      return 0;
    }
    return ta < tb ? -1 : 1;
  });
}

/**
 * True when property tax information was retrieved for the job.
 * Requirements: 6.10
 */
export function hasPropertyTaxInformation(
  info: PropertyTaxInformation | null | undefined
): boolean {
  return info !== null && info !== undefined;
}

/**
 * True when the property tax record has at least one installment.
 * Requirements: 6.12
 */
export function hasInstallments(
  info: PropertyTaxInformation | null | undefined
): boolean {
  return !!info && Array.isArray(info.installments) && info.installments.length > 0;
}

/**
 * True when at least one deed was found for the property.
 * Requirements: 7.9
 */
export function hasDeeds(deeds: DeedRecord[] | null | undefined): boolean {
  return Array.isArray(deeds) && deeds.length > 0;
}

/**
 * Empty-state message for the Property Tax Information section, or null when
 * information is present.
 * Requirements: 6.10
 */
export function propertyTaxEmptyState(
  info: PropertyTaxInformation | null | undefined
): string | null {
  return hasPropertyTaxInformation(info) ? null : NO_TAX_INFO_MESSAGE;
}

/**
 * Empty-state message for the installments area, or null when installments are
 * present.
 * Requirements: 6.12
 */
export function installmentsEmptyState(
  info: PropertyTaxInformation | null | undefined
): string | null {
  return hasInstallments(info) ? null : NO_INSTALLMENTS_MESSAGE;
}

/**
 * Empty-state message for the Deed Information section, or null when deeds are
 * present.
 * Requirements: 7.9
 */
export function deedsEmptyState(deeds: DeedRecord[] | null | undefined): string | null {
  return hasDeeds(deeds) ? null : NO_DEEDS_MESSAGE;
}
